import React, { useEffect, useState, Suspense, lazy } from 'react';
import { LanguageProvider } from './contexts/LanguageContext';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Mission, MissionPanels } from './components/Mission';
import { ConsularBanner } from './components/ConsularBanner';
import { LandingPage } from './components/LandingPage';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import { useLanguage } from './contexts/LanguageContext';
// Lazy-loaded below-the-fold sections
const CommunityStrength = lazy(() => import('./components/CommunityStrength').then(m => ({
  default: m.CommunityStrength
})));
const Leadership = lazy(() => import('./components/Leadership').then(m => ({
  default: m.Leadership
})));
const AdvisoryCouncil = lazy(() => import('./components/AdvisoryCouncil').then(m => ({
  default: m.AdvisoryCouncil
})));
const MakeDifferenceCTA = lazy(() => import('./components/MakeDifferenceCTA').then(m => ({
  default: m.MakeDifferenceCTA
})));
const NewsBlog = lazy(() => import('./components/NewsBlog').then(m => ({
  default: m.NewsBlog
})));
const MediaScroller = lazy(() => import('./components/MediaScroller').then(m => ({
  default: m.MediaScroller
})));
const ConsularAffairs = lazy(() => import('./components/ConsularAffairs').then(m => ({
  default: m.ConsularAffairs
})));
const BusinessInvestments = lazy(() => import('./components/BusinessInvestments').then(m => ({
  default: m.BusinessInvestments
})));
const OurPeopleStories = lazy(() => import('./components/OurPeopleStories').then(m => ({
  default: m.OurPeopleStories
})));
const NonprofitDirectory = lazy(() => import('./components/NonprofitDirectory').then(m => ({
  default: m.NonprofitDirectory
})));
const ExploreHonduras = lazy(() => import('./components/ExploreHonduras').then(m => ({
  default: m.ExploreHonduras
})));
const HondurasQuiz = lazy(() => import('./components/HondurasQuiz').then(m => ({
  default: m.HondurasQuiz
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const SocialMediaVoices = lazy(() => import('./components/SocialMediaVoices').then(m => ({
  default: m.SocialMediaVoices
})));
const ChatbaseWidget = lazy(() => import('./components/ChatbaseWidget').then(m => ({
  default: m.ChatbaseWidget
})));
// Lazy-loaded sub-pages
const AdminDashboard = lazy(() => import('./components/AdminDashboard').then(m => ({
  default: m.AdminDashboard
})));
const ConsularPage = lazy(() => import('./pages/ConsularPage').then(m => ({
  default: m.ConsularPage
})));
const BusinessMapPage = lazy(() => import('./pages/BusinessMapPage').then(m => ({
  default: m.BusinessMapPage
})));
const MemberProfilePage = lazy(() => import('./pages/MemberProfilePage').then(m => ({
  default: m.MemberProfilePage
})));
const NewsStoryPage = lazy(() => import('./pages/NewsStoryPage').then(m => ({
  default: m.NewsStoryPage
})));
const MembersDirectoryPage = lazy(() => import('./pages/MembersDirectoryPage').then(m => ({
  default: m.MembersDirectoryPage
})));
const SaveTheDatePage = lazy(() => import('./pages/SaveTheDatePage').then(m => ({
  default: m.SaveTheDatePage
})));
// Lightweight loading placeholder — subtle pulse instead of spinner to reduce visual jank
function SectionLoader() {
  return <div className="w-full py-16 flex items-center justify-center" data-id="element-0">
      <div className="flex items-center gap-2" data-id="element-1">
        <div className="w-2 h-2 rounded-full bg-haa-blue/30 animate-pulse" data-id="element-2" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/20 animate-pulse" style={{
        animationDelay: '150ms'
      }} data-id="element-3" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/10 animate-pulse" style={{
        animationDelay: '300ms'
      }} data-id="element-4" />
      </div>
    </div>;
}
function AppContent({
  showLanding,
  setShowLanding
}: {
  showLanding: boolean;
  setShowLanding: (v: boolean) => void;
}) {
  const {
    setLanguage
  } = useLanguage();
  const [pendingScroll, setPendingScroll] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const hash = window.location.hash;
    return isSectionAnchor(hash) ? resolveAnchor(hash.slice(1)) : null;
  });
  const handleSelectLanguage = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
  };
  const handleConsularAccess = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
    setPendingScroll('consular-affairs');
  };
  // React to in-page hash changes (e.g. opening a /#leadership link while already on the site)
  useEffect(() => {
    const handler = () => {
      const hash = window.location.hash;
      if (isSectionAnchor(hash)) {
        setShowLanding(false);
        setPendingScroll(resolveAnchor(hash.slice(1)));
      }
    };
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, [setShowLanding]);
  useEffect(() => {
    if (!showLanding && pendingScroll) {
      // Lazy-loaded sections may not be mounted yet — retry briefly until the element exists.
      const start = Date.now();
      let cancelled = false;
      const tryScroll = () => {
        if (cancelled) return;
        const el = document.getElementById(pendingScroll);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
          setPendingScroll(null);
          return;
        }
        if (Date.now() - start < 4000) {
          setTimeout(tryScroll, 150);
        } else {
          setPendingScroll(null);
        }
      };
      const timer = setTimeout(tryScroll, 100);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }
  }, [showLanding, pendingScroll]);
  if (showLanding) {
    return <LandingPage onSelectLanguage={handleSelectLanguage} onConsularAccess={handleConsularAccess} data-id="element-5" />;
  }
  return <div className="font-nunito w-full min-h-screen overflow-x-hidden" data-id="element-6">
      <Navbar data-id="element-7" />
      <main data-id="element-8">
        <Hero data-id="element-9" />
        <ConsularBanner data-id="element-10" />
        <Mission data-id="element-11" />

        {/* Each section loads independently for faster perceived performance */}
        <Suspense fallback={<SectionLoader data-id="element-13" />} data-id="element-12">
          <OurPeopleStories data-id="element-14" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-16" />} data-id="element-15">
          <MakeDifferenceCTA data-id="element-17" />
        </Suspense>
        <div className="max-w-5xl mx-auto px-4" data-id="element-18">
          <div className="h-px bg-gradient-to-r from-transparent via-haa-blue/20 to-transparent" data-id="element-19" />
        </div>
        <Suspense fallback={<SectionLoader data-id="element-21" />} data-id="element-20">
          <NewsBlog data-id="element-22" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-24" />} data-id="element-23">
          <MediaScroller data-id="element-25" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-27" />} data-id="element-26">
          <CommunityStrength data-id="element-28" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-30" />} data-id="element-29">
          <MissionPanels data-id="element-31" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-33" />} data-id="element-32">
          <Leadership data-id="element-34" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-36" />} data-id="element-35">
          <AdvisoryCouncil data-id="element-37" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-39" />} data-id="element-38">
          <MakeDifferenceCTA data-id="element-40" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-42" />} data-id="element-41">
          <BusinessInvestments data-id="element-43" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-45" />} data-id="element-44">
          <ConsularAffairs data-id="element-46" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-48" />} data-id="element-47">
          <SocialMediaVoices data-id="element-49" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-51" />} data-id="element-50">
          <NonprofitDirectory data-id="element-52" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-54" />} data-id="element-53">
          <HondurasQuiz data-id="element-55" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-57" />} data-id="element-56">
          <ExploreHonduras data-id="element-58" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-60" />} data-id="element-59">
          <FAQ data-id="element-61" />
        </Suspense>
      </main>
      <Suspense fallback={null} data-id="element-62">
        <Footer data-id="element-63" />
      </Suspense>
      <Suspense fallback={null} data-id="element-64">
        <ChatbaseWidget data-id="element-65" />
      </Suspense>
    </div>;
}
// Short, shareable aliases that resolve to the actual section IDs in the DOM.
// Example: /#council scrolls to the <section id="advisory-council">.
const SECTION_ALIASES: Record<string, string> = {
  council: 'advisory-council',
  members: 'our-stories',
  consular: 'consular-affairs',
  business: 'business-investments',
  nonprofit: 'nonprofit-directory',
  explore: 'explore-honduras',
  trivia: 'honduras-trivia',
  socials: 'social-voices'
};
// Section anchors that, when present in the URL hash, should bypass the landing page
// and scroll directly to that section on the main site. Both canonical IDs and the
// friendly aliases above are accepted.
const SECTION_ANCHORS = new Set(['home', 'mission', 'community', 'leadership', 'advisory-council', 'our-stories', 'news', 'social-voices', 'explore-honduras', 'honduras-trivia', 'nonprofit-directory', 'business-investments', 'consular-affairs', 'faq', 'contact', ...Object.keys(SECTION_ALIASES)]);
function resolveAnchor(id: string): string {
  return SECTION_ALIASES[id] ?? id;
}
function isSectionAnchor(hash: string): boolean {
  if (!hash || !hash.startsWith('#')) return false;
  if (hash.startsWith('#/')) return false;
  const id = hash.slice(1);
  return SECTION_ANCHORS.has(id);
}
export function App() {
  const initialHash = typeof window !== 'undefined' ? window.location.hash : '';
  const [showLanding, setShowLanding] = useState(!isSectionAnchor(initialHash));
  const [showAdmin, setShowAdmin] = useState(() => window.location.hash === '#/admin');
  const [showConsular, setShowConsular] = useState(() => window.location.hash === '#/consular');
  const [showBusinessMap, setShowBusinessMap] = useState(() => window.location.hash.startsWith('#/business-map'));
  const [businessMapLang, setBusinessMapLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/business-map')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  const [showMembers, setShowMembers] = useState(() => window.location.hash === '#/members');
  const [showSaveTheDate, setShowSaveTheDate] = useState(() => window.location.hash === '#/save-the-date');
  const [showMember, setShowMember] = useState(() => window.location.hash.startsWith('#/member/'));
  const [memberSlug, setMemberSlug] = useState(() => {
    const hash = window.location.hash;
    return hash.startsWith('#/member/') ? hash.replace('#/member/', '') : '';
  });
  const [showNews, setShowNews] = useState(() => window.location.hash.startsWith('#/news/'));
  const [newsPostId, setNewsPostId] = useState(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return '';
    const raw = hash.replace('#/news/', '');
    return raw.split('?')[0];
  });
  const [newsLang, setNewsLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      setShowAdmin(hash === '#/admin');
      setShowConsular(hash === '#/consular');
      const isBusinessMap = hash.startsWith('#/business-map');
      setShowBusinessMap(isBusinessMap);
      if (isBusinessMap) {
        const bmLangMatch = hash.match(/[?&]lang=(EN|ES)/);
        setBusinessMapLang(bmLangMatch ? bmLangMatch[1] as 'EN' | 'ES' : 'EN');
      }
      setShowMembers(hash === '#/members');
      setShowSaveTheDate(hash === '#/save-the-date');
      const isMember = hash.startsWith('#/member/');
      setShowMember(isMember);
      if (isMember) {
        setMemberSlug(hash.replace('#/member/', ''));
      }
      const isNews = hash.startsWith('#/news/');
      setShowNews(isNews);
      if (isNews) {
        const raw = hash.replace('#/news/', '');
        setNewsPostId(raw.split('?')[0]);
        const langMatch = raw.match(/[?&]lang=(EN|ES)/);
        setNewsLang(langMatch ? langMatch[1] as 'EN' | 'ES' : 'EN');
      }
      // When navigating back to root from a sub-page, skip the landing page
      if (hash === '' || hash === '#') {
        setShowLanding(false);
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);
  const handleReturnToLanding = () => {
    setShowLanding(true);
  };
  if (showAdmin) {
    return <Suspense fallback={<SectionLoader data-id="element-67" />} data-id="element-66">
        <AdminDashboard onBack={() => {
        window.location.hash = '';
        setShowAdmin(false);
      }} data-id="element-68" />
      </Suspense>;
  }
  if (showConsular) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-69">
        <Suspense fallback={<SectionLoader data-id="element-71" />} data-id="element-70">
          <ConsularPage data-id="element-72" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showBusinessMap) {
    return <LanguageProvider initialLanguage={businessMapLang} onLanguageSwitch={handleReturnToLanding} data-id="element-73">
        <Suspense fallback={<SectionLoader data-id="element-75" />} data-id="element-74">
          <BusinessMapPage data-id="element-76" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMembers) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-77">
        <Suspense fallback={<SectionLoader data-id="element-79" />} data-id="element-78">
          <MembersDirectoryPage data-id="element-80" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showSaveTheDate) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-81">
        <Suspense fallback={<SectionLoader data-id="element-83" />} data-id="element-82">
          <SaveTheDatePage data-id="element-84" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMember) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-85">
        <Suspense fallback={<SectionLoader data-id="element-87" />} data-id="element-86">
          <MemberProfilePage slug={memberSlug} data-id="element-88" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNews) {
    return <LanguageProvider initialLanguage={newsLang} data-id="element-89">
        <Suspense fallback={<SectionLoader data-id="element-91" />} data-id="element-90">
          <NewsStoryPage postId={newsPostId} data-id="element-92" />
        </Suspense>
      </LanguageProvider>;
  }
  return <>
      <GoogleAnalytics data-id="element-93" />
      <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-94">
        <AppContent showLanding={showLanding} setShowLanding={setShowLanding} data-id="element-95" />
      </LanguageProvider>
    </>;
}